<template>
  <CDataTable
    :items="normalizedItems"
    :fields="fields"
    :loading="loading"
    hover
    border
    sorter
    responsive
    :sorter-value="{ column: 'receivedAt', asc: false }"
    :items-per-page="10"
    :pagination="{ align: 'center' }"
  >
    <template #callId="{ item }">
      <td>
        <div class="d-flex align-items-center text-nowrap">
          <router-link
            :to="{ name: 'FraudDetails', params: { id: item.callId } }"
            :title="item.callId"
            class="text-monospace"
          >
            {{ truncateCallId(item.callId) }}
          </router-link>
          <CIcon
            name="cil-copy"
            class="ml-1 text-muted"
            style="cursor: pointer;"
            @click.native="copyCallId(item.callId)"
          />
          <a
            v-if="item.notifyAdmins"
            :href="slackLink(item.flagCategory)"
            title="Search for this call ID in Slack channel"
            class="ml-2"
            style="margin-top: -3px;"
          >
            <CIcon
              name="cib-slack"
              class="text-info"
            />
          </a>
        </div>
        <div
          v-if="item.callerId"
          class="small text-muted"
        >
          {{ item.callerId }}
        </div>
        <CBadge
          v-if="item.status === 'Addressed'"
          class="mt-1 badge-status-addressed"
        >
          Addressed
        </CBadge>
        <CBadge
          v-else-if="item.status === 'Ignored'"
          class="mt-1 badge-status-ignored"
        >
          Ignored
        </CBadge>
      </td>
    </template>

    <template #receivedAt="{ item }">
      <td>
        {{ formatDateTime(item.receivedAt) }}
      </td>
    </template>

    <template #filterTags="{ item }">
      <td>
        <span
          v-for="(tag, idx) in splitTags(item.filterTags)"
          :key="idx"
        >
          <CBadge
            color="secondary"
            class="mr-1"
            :title="tagDescription(tag)"
          >{{ formatTag(tag) }}</CBadge>
        </span>
      </td>
    </template>

    <template #actions="{ item }">
      <td class="text-center">
        <CIcon
          name="cil-check-circle"
          :class="['action-icon', item.status === 'Addressed' ? 'action-icon--disabled' : 'action-icon--addressed']"
          title="Mark as Addressed"
          @click.native="item.status !== 'Addressed' && $emit('status-update', item, 'Addressed')"
        />
        <CIcon
          name="cil-ban"
          :class="['action-icon ml-2', item.status === 'Ignored' ? 'action-icon--disabled' : 'action-icon--ignored']"
          title="Mark as Ignored"
          @click.native="item.status !== 'Ignored' && $emit('status-update', item, 'Ignored')"
        />
      </td>
    </template>
  </CDataTable>
</template>

<script>
import moment from 'moment-timezone'
import { showSuccessMessage } from '@/notification-utils'
import FILTER_TAG_DESCRIPTIONS from './filterTagDescriptions'

const STATUS_MAP = {
  0: 'New',
  1: 'Addressed',
  2: 'Ignored'
}

function normalizeStatus(status) {
  if (typeof status === 'number') {
    return STATUS_MAP[status] || 'New'
  }

  return status
}

const FIELDS = [
  { key: 'callId', label: 'Call ID', _style: 'min-width: 160px;' },
  { key: 'receivedAt', label: 'Date', _style: 'min-width: 140px;' },
  { key: 'sellerName', label: 'Seller' },
  { key: 'buyerName', label: 'Buyer' },
  { key: 'categoryName', label: 'Category Name' },
  { key: 'filterTags', label: 'Filter Tags', sorter: false },
  { key: 'actions', label: 'Actions', sorter: false, _style: 'min-width: 80px; text-align: center;' },
]

export default {
  name: 'FlaggedCallsTable',

  props: {
    items: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    slackLink: { type: Function, required: true },
  },

  data() {
    return {
      fields: FIELDS,
    }
  },

  computed: {
    normalizedItems() {
      return this.items.map(item => ({
        ...item,
        status: normalizeStatus(item.status)
      }))
    },
  },

  methods: {
    formatDateTime(date) {
      return moment(date).format('MMM DD, YYYY, h:mm A')
    },

    truncateCallId(callId) {
      if (!callId || callId.length <= 16) {
        return callId
      }

      return callId.substring(0, 8) + '...'
    },

    copyCallId(callId) {
      navigator.clipboard.writeText(callId)
      showSuccessMessage('Call ID copied to clipboard')
    },

    formatTag(tag) {
      return tag.replace(/_/g, ' ')
    },

    tagDescription(tag) {
      return FILTER_TAG_DESCRIPTIONS[tag] || ''
    },

    splitTags(tags) {
      if (!tags) {
        return []
      }

      return tags.split(',').map(t => t.trim()).filter(t => t)
    },
  },
}
</script>

<style scoped>
.action-icon {
  cursor: pointer;
  transition: color 0.15s;
}
.action-icon--addressed {
  color: #2eb85c;
}
.action-icon--addressed:hover {
  color: #1e9e4a;
}
.action-icon--ignored {
  color: #e55353;
}
.action-icon--ignored:hover {
  color: #c83232;
}
.action-icon--disabled {
  color: #d0d0d0;
  cursor: default;
  pointer-events: none;
}
.badge-status-addressed {
  background-color: #2eb85c;
  color: #fff;
}
.badge-status-ignored {
  background-color: #636f83;
  color: #fff;
}
</style>
